import * as React from "react";
import { useState } from "react";
import {
  DayPicker,
  DateRange,
  defaultLocale,
  MonthCaptionProps,
} from "react-day-picker";
import "react-day-picker/style.css";

const days = ["П", "В", "С", "Ч", "П", "С", "В"];
const months = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];
const years: number[] = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - 5 + i
);

const locale = {
  localize: {
    ...defaultLocale.localize,
    day: (n: number) => days[n],
    month: (n: number) => months[n],
  },
};

interface CustomCaptionProps extends MonthCaptionProps {
  setMonth: (month: Date) => void;
}

const CustomCaption: React.FC<CustomCaptionProps> = ({
  calendarMonth,
  setMonth,
}) => {
  const [isOpenMonth, setIsOpenMonth] = useState(false);
  const [isOpenYear, setIsOpenYear] = useState(false);

  const currentYear = calendarMonth.date.getFullYear();
  const currentMonth = calendarMonth.date.getMonth();

  return (
    <div className="flex justify-between items-center py-2 relative gap-[8px]">
      <div className="relative w-[50%]">
        <button
          type="button"
          onClick={() => setIsOpenMonth(!isOpenMonth)}
          className="text-left px-4 py-2 bg-[#FAFAFA] rounded-[10px] text-gray-900 w-full border border-[#C7D0D1] border-solid"
        >
          {months[currentMonth]}
        </button>
        {isOpenMonth && (
          <ul className="absolute top-full left-0 w-full bg-white border shadow-lg rounded-md mt-1 z-50">
            {months.map((month, index) => (
              <li
                key={index}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setMonth(new Date(currentYear, index));
                  setIsOpenMonth(false);
                }}
              >
                {month}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="relative w-[50%]">
        <button
          type="button"
          onClick={() => setIsOpenYear(!isOpenYear)}
          className="text-left px-4 py-2 bg-[#FAFAFA] rounded-[10px] text-gray-900 w-full border border-[#C7D0D1] border-solid"
        >
          {currentYear}
        </button>
        {isOpenYear && (
          <ul className="absolute top-full left-0 w-full bg-white border shadow-lg rounded-md mt-1 z-50">
            {years.map((year) => (
              <li
                key={year}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setMonth(new Date(year, currentMonth));
                  setIsOpenYear(false);
                }}
              >
                {year}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

type CalendarProps = {
  range?: DateRange;
  setRange?: (range: DateRange | undefined) => void;
  // onChange: (range: DateRange | undefined) => void,
  max?: number;
};
export function Calendar({ range, setRange, max }: CalendarProps) {
  const [month, setMonth] = useState<Date>(new Date());

  return (
    <div className="w-full flex flex-col gap-[8px]">
      <label htmlFor="date">Дата</label>

      <DayPicker
        id="date"
        mode="range"
        month={month}
        selected={range}
        onSelect={setRange}
        max={max}
        locale={locale}
        captionLayout="dropdown"
        className="border-0"
        classNames={{
          root: "w-full",
          months: "w-full",
          month: "flex flex-col gap-[8px]",
          caption: "flex justify-center py-2 mb-4 relative items-center",
          caption_label:
            "text-sm font-medium text-gray-900 w-full flex justify-between",
          nav: "hidden",
          table: "w-full border-collapse",
          head_row: "flex font-medium text-gray-900",
          head_cell: "m-0.5 w-9 font-normal text-sm",
          row: "flex w-full mt-2",
          cell: "w-full text-gray-600 rounded-md h-9 w-9 text-center text-sm p-0 m-0.5 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-gray-900/20 [&:has([aria-selected].day-outside)]:text-white [&:has([aria-selected])]:bg-gray-900/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
          dropdowns: "w-full flex justify-between",
          dropdown_root:
            "relative px-[10px] py-[8px] flex justify-between w-[calc(50%-8px)] border border-[#C7D0D1] border-solid bg-[#FAFAFA] rounded-[10px]",
          month_grid:
            "border border-[#C7D0D1] border-solid rounded-[10px] overflow-hidden bg-[#F1F3F3]",

          weekday: "text-center py-[10px]",
          weekdays: "max-h-[32px] bg-[#FAFAFA]",
          weeks: "w-full",
          day_button: "flex items-center justify-center w-full h-full",
          day: "w-9 h-9 transition-all hover:bg-gray-200",
          day_selected: "bg-green-500 text-white font-bold",

          day_today: "relative bg-gray-200",
          day_outside: "text-gray-400",
        }}
        modifiersClassNames={{
          selected: "bg-[#1F8D7D] text-white font-bold",
          range_start: "bg-[#1F8D7D] text-white rounded-l-md",
          range_end: "bg-[#1F8D7D] text-white rounded-r-md",
          range_middle: "bg-[#C8EAE5] text-white rounded-0",
        }}
        timeZone="UTC"
        components={{
          MonthCaption: (props) => (
            <CustomCaption {...props} setMonth={setMonth} />
          ), // ✅ Используем `MonthCaption`
        }}
      />
    </div>
  );
}
